import React, { ComponentType, ReactElement } from "react";
import {
  ApproveIcon,
  CheckCircleIcon,
  CircleCancelIcon,
  CircleCheckIcon,
  CreditCardIcon,
  RefreshIcon,
  TruckIcon,
} from "../../../../../../components/Icons";
import { EOrderStatus } from "../../../../../../services/orders/orders.interface";
import StatusChip from "../../../components/StatusChip/StatusChip";
import { useTheme } from "@mui/material";

interface IOrderStatusProps {
  status: EOrderStatus;
}

interface IStatusTheme {
  badgeColor: string;
  color: string;
}

interface IIconProps {
  width?: number;
  height?: number;
  color?: string;
}

const getStatusTheme = (theme: any): Record<EOrderStatus, IStatusTheme> => ({
  [EOrderStatus.APPROVAL_NEEDED]: { badgeColor: theme.palette.primary.light, color: theme.palette.warning.dark },
  [EOrderStatus.APPROVED]: { badgeColor: theme.palette.green.lighter, color: theme.palette.green.dark },
  [EOrderStatus.COMPLETED]: { badgeColor: theme.palette.green.lighter, color: theme.palette.green.dark },
  [EOrderStatus.CANCELED]: { badgeColor: theme.palette.red.lighter, color: theme.palette.red.dark },
  [EOrderStatus.PAYMENT_FAILED]: { badgeColor: theme.palette.red.lighter, color: theme.palette.red.dark },
  [EOrderStatus.SHIPPED]: { badgeColor: theme.palette.violet.lighter, color: theme.palette.violet.dark },
  [EOrderStatus.TRIAGED]: { badgeColor: theme.palette.neutral.main, color: theme.palette.black.main },
});

const DISPLAY_NAMES: Record<EOrderStatus, string> = {
  [EOrderStatus.APPROVAL_NEEDED]: "Approval Needed",
  [EOrderStatus.APPROVED]: "Approved",
  [EOrderStatus.COMPLETED]: "Completed",
  [EOrderStatus.CANCELED]: "Canceled",
  [EOrderStatus.PAYMENT_FAILED]: "Payment Failed",
  [EOrderStatus.SHIPPED]: "Shipped",
  [EOrderStatus.TRIAGED]: "Triaged",
};

const STATUS_ICONS: Record<EOrderStatus, ComponentType<IIconProps>> = {
  [EOrderStatus.APPROVAL_NEEDED]: ApproveIcon,
  [EOrderStatus.APPROVED]: CheckCircleIcon,
  [EOrderStatus.COMPLETED]: CircleCheckIcon,
  [EOrderStatus.CANCELED]: CircleCancelIcon,
  [EOrderStatus.PAYMENT_FAILED]: CreditCardIcon,
  [EOrderStatus.SHIPPED]: TruckIcon,
  [EOrderStatus.TRIAGED]: RefreshIcon,
};

const getStatusIcon = (status: EOrderStatus, color: string): ReactElement => {
  const IconComponent = STATUS_ICONS[status] || RefreshIcon;
  return <IconComponent width={12} height={12} color={color} />;
};

const OrderStatus = ({ status }: IOrderStatusProps) => {
  const theme = useTheme();
  const STATUS_THEME = getStatusTheme(theme);

  const { badgeColor, color } = STATUS_THEME[status] || {
    badgeColor: theme.palette.neutral.main,
    color: theme.palette.black.main,
  };
  const displayName = DISPLAY_NAMES[status] || status;
  const icon = getStatusIcon(status, color);

  return <StatusChip label={displayName} badgeColor={badgeColor} color={color} icon={icon} />;
};

export default OrderStatus;
